/**
 * useSendGift
 *
 * Local-state hook for the SendGiftSheet:
 * - loads gifts on mount via FetchGiftsUseCase
 * - tracks selectedIndex / instances; tapping the same gift increments instances
 * - exposes shouldRecharge (wallet vs price) and executeSend
 *
 * Flutter: SendGiftBottomSheetState
 */
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { selectWalletCoins } from '@/presentation/store/slices/wallet/walletSlice';
import { giftService } from '@/domain/services/gift/giftService';
import { FetchGiftsUseCase } from '@/domain/usecases/gift/fetchGiftsUseCase';
import { SendContentGiftUseCase } from '@/domain/usecases/gift/sendContentGiftUseCase';
import { SendChatGiftUseCase } from '@/domain/usecases/gift/sendChatGiftUseCase';
import { SendVideoCallGiftUseCase } from '@/domain/usecases/gift/sendVideoCallGiftUseCase';
import { SendLiveStreamingGiftUseCase } from '@/domain/usecases/gift/sendLiveStreamingGiftUseCase';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { SendGiftTargetType } from '@/domain/enums/gift/sendGiftTargetType';
import { appLogger } from '@/domain/services/analytics/appLogger';

const fetchGiftsUseCase = new FetchGiftsUseCase(giftService);
const sendContentGiftUseCase = new SendContentGiftUseCase(giftService);
const sendChatGiftUseCase = new SendChatGiftUseCase(giftService);
const sendVideoCallGiftUseCase = new SendVideoCallGiftUseCase(giftService);
const sendLiveStreamingGiftUseCase = new SendLiveStreamingGiftUseCase(giftService);

/**
 * Where the gift is being sent:
 * - `media`     — content gift on a UserMedia (builds mediaInfo + notification).
 * - `chat`      — chat gift to a resolved peer user (builds chatInfo, no notification).
 * - `videoCall` — gift inside a 1:1 chat video call (builds chatVideoCallInfo, no
 *                 notification). Carries both call users + the call message id; the
 *                 service resolves the peer from caller/audience vs `currentUser`.
 * - `liveStreaming` — audience "tip" during a community live session (builds
 *                 broadcastingInfo, null notification). The peer is the broadcaster,
 *                 identified directly by firebase id + name; carries the live
 *                 message id shared with the liveness listener
 *                 (`live_streaming_screen.dart:1397-1430`).
 */
export type SendGiftTarget =
  | { kind: SendGiftTargetType.media; media: UserMediaEntity }
  | { kind: SendGiftTargetType.chat; peerUser: UserEntity }
  | {
      kind: SendGiftTargetType.videoCall;
      callerUser: UserEntity;
      audienceUser: UserEntity;
      callMessageId: string;
    }
  | {
      kind: SendGiftTargetType.liveStreaming;
      broadcasterFirebaseId: string;
      broadcasterName: string;
      liveStreamingMessageId: string;
    };

export function useSendGift(target: SendGiftTarget, defaultGift?: GiftEntity) {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const walletCoins = useSelector(selectWalletCoins);

  const [gifts, setGifts] = useState<GiftEntity[]>([]);
  const [isLoadingGifts, setIsLoadingGifts] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [instances, setInstances] = useState<number>(0);
  const [isSending, setIsSending] = useState(false);
  const [initialScrollIndex, setInitialScrollIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchGiftsUseCase.execute();
        if (!cancelled) {
          setGifts(list);
          if (defaultGift) {
            const index = list.findIndex(g => g.id === defaultGift.id);
            if (index !== -1) {
              setSelectedIndex(index);
              setInstances(1);
              setInitialScrollIndex(index);
            }
          }
        }
      } catch (error) {
        appLogger.error('Failed to load gifts', 'useSendGift.loadGifts', { error: error as Error });
      } finally {
        if (!cancelled) setIsLoadingGifts(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedGift = selectedIndex >= 0 ? gifts[selectedIndex] : undefined;
  const totalCoins = selectedGift ? selectedGift.coins * instances : 0;
  const shouldRecharge = !!selectedGift && walletCoins < totalCoins;

  const selectGift = useCallback((index: number) => {
    if (selectedIndex !== index) {
      setSelectedIndex(index);
      setInstances(1);
    } else {
      setInstances(i => i + 1);
    }
  }, [selectedIndex]);

  const executeSend = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !selectedGift || instances <= 0) return false;
    setIsSending(true);
    try {
      if (target.kind === SendGiftTargetType.chat) {
        return await sendChatGiftUseCase.execute({
          currentUser,
          peerUser: target.peerUser,
          gift: selectedGift,
          instances,
        });
      }
      if (target.kind === SendGiftTargetType.videoCall) {
        return await sendVideoCallGiftUseCase.execute({
          currentUser,
          callerUser: target.callerUser,
          audienceUser: target.audienceUser,
          callMessageId: target.callMessageId,
          gift: selectedGift,
          instances,
        });
      }
      if (target.kind === SendGiftTargetType.liveStreaming) {
        return await sendLiveStreamingGiftUseCase.execute({
          currentUser,
          broadcasterFirebaseId: target.broadcasterFirebaseId,
          broadcasterName: target.broadcasterName,
          liveStreamingMessageId: target.liveStreamingMessageId,
          gift: selectedGift,
          instances,
        });
      }
      return await sendContentGiftUseCase.execute({
        media: target.media,
        currentUser,
        gift: selectedGift,
        instances,
      });
    } catch (error) {
      appLogger.error('Failed to send gift', 'useSendGift.executeSend', { error: error as Error });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [currentUser, selectedGift, instances, target]);

  return {
    gifts,
    isLoadingGifts,
    selectedIndex,
    instances,
    shouldRecharge,
    isSending,
    initialScrollIndex,
    selectGift,
    executeSend,
  };
}
